/**
 * Keep a name/number that would cross its panel's cut edge on the panel.
 *
 * A long name ("SENATI-MARTINEZ", order 28404) is sized by its letter height,
 * so it runs past the back panel's sides — into the seam allowance or off the
 * piece. When a name or number crosses the panel edge it is squeezed sideways
 * (its height is kept) until it sits TEXT_SIDE_MARGIN_CM inside both edges,
 * measured against the real cut silhouette across the text's own height band
 * (a back narrows towards the armholes). A text that already fits that width
 * but sits too far to one side is only moved inward. Texts inside the panel
 * are left exactly as the customer placed them.
 */
export const TEXT_SIDE_MARGIN_CM = 2.5;

export type Polyline = { points: Array<[number, number]> };

/** x positions where the horizontal line at `y` crosses the outlines, sorted. */
function crossings(polylines: Polyline[], y: number): number[] {
  const xs: number[] = [];
  for (const { points } of polylines) {
    const n = points.length;
    if (n < 3) continue;
    for (let i = 0; i < n; i++) {
      const [x1, y1] = points[i];
      const [x2, y2] = points[(i + 1) % n];
      // half-open so a vertex shared by two edges counts once
      if (y1 <= y !== y2 <= y) xs.push(x1 + ((y - y1) * (x2 - x1)) / (y2 - y1));
    }
  }
  return xs.sort((a, b) => a - b);
}

/** The inside interval (even-odd) of the outlines at height `y` that contains `x`. */
function intervalAt(polylines: Polyline[], x: number, y: number): [number, number] | null {
  const xs = crossings(polylines, y);
  for (let i = 0; i + 1 < xs.length; i += 2) {
    if (x >= xs[i] && x <= xs[i + 1]) return [xs[i], xs[i + 1]];
  }
  return null;
}

export function pointInPolylines(polylines: Polyline[], x: number, y: number): boolean {
  return intervalAt(polylines, x, y) !== null;
}

/**
 * The panel's left/right cut edges around `x` across the band [y0, y1]: the
 * narrowest inside interval met at evenly spaced heights. Heights where `x`
 * lies outside the panel (the band pokes above/below it) are ignored.
 */
export function panelBandEdges(
  polylines: Polyline[],
  x: number,
  y0: number,
  y1: number,
  samples = 11
): { left: number; right: number } | null {
  let left = -Infinity;
  let right = Infinity;
  let hits = 0;
  for (let i = 0; i < samples; i++) {
    const y = samples === 1 ? (y0 + y1) / 2 : y0 + ((y1 - y0) * i) / (samples - 1);
    const span = intervalAt(polylines, x, y);
    if (!span) continue;
    hits++;
    left = Math.max(left, span[0]);
    right = Math.min(right, span[1]);
  }
  return hits ? { left, right } : null;
}

export type TextFit =
  { kind: 'inside' } | { kind: 'no-room' } | { kind: 'fitted'; scaleX: number; cx: number };

/**
 * What to do with a text whose box spans [left, right] (centre `cx`) between
 * panel edges `edges`. Only a text crossing an edge (by more than
 * `tolerance`) is touched: squeezed to the room between the margins when it
 * is wider than that, and moved so it keeps `margin` from both edges.
 */
export function fitTextToPanel(
  box: { left: number; right: number; cx: number },
  edges: { left: number; right: number },
  margin: number,
  tolerance = 0
): TextFit {
  const crosses = box.left < edges.left - tolerance || box.right > edges.right + tolerance;
  if (!crosses) return { kind: 'inside' };
  const room = edges.right - edges.left - 2 * margin;
  const width = box.right - box.left;
  if (room <= 0 || width <= 0) return { kind: 'no-room' };
  const fitted = Math.min(width, room);
  const lo = edges.left + margin + fitted / 2;
  const hi = edges.right - margin - fitted / 2;
  return { kind: 'fitted', scaleX: fitted / width, cx: Math.min(Math.max(box.cx, lo), hi) };
}
